import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [ready, setReady] = useState(false);

  const link = useMemo(() => {
    if (typeof window === "undefined") return { type: "", email: "" };
    const hash = window.location.hash.replace(/^#/, "");
    const hashParams = new URLSearchParams(hash);
    const searchParams = new URLSearchParams(window.location.search);
    const type = hashParams.get("type") || searchParams.get("type");
    const email = searchParams.get("email") || "";
    return { type, email };
  }, []);
  const isInvite = link.type === "invite";
  const needsCode = link.type === "recovery" && Boolean(link.email);

  useEffect(() => {
    // Supabase auto-processes the token from the URL hash and fires an event
    // on the auth listener (PASSWORD_RECOVERY for resets, SIGNED_IN for invites).
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN" || event === "INITIAL_SESSION") {
        if (session) setReady(true);
      }
    });
    // Also check current session in case event fired before mount.
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setReady(true);
    });
    return () => subscription.unsubscribe();
  }, []);

  const title = isInvite ? "Create your password" : "Set a new password";
  const submitLabel = isInvite ? "Create password" : "Update password";
  const submittingLabel = isInvite ? "Creating…" : "Updating…";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    let err: Error | null = null;
    if (!ready && needsCode) {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        email: link.email,
        token: code.trim(),
        type: "recovery",
      });
      err = verifyError;
      if (!err) setReady(true);
    }
    if (!err) {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      err = updateError;
    }
    setSubmitting(false);
    if (err) {
      setError(err.message);
      return;
    }
    toast({
      title: isInvite ? "Account activated" : "Password updated",
      description: isInvite
        ? "Welcome! You're now signed in."
        : "You can now sign in with your new password.",
    });
    // Full-page navigation so AuthProvider re-mounts and the role lookup
    // completes before RequireAdmin renders — avoids the "Not authorized" race.
    window.location.assign("/admin");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <Helmet>
        <title>{title} · AI-labbet</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">AI-labbet</div>
          <h1 className="text-2xl font-semibold mt-1">{title}</h1>
          {isInvite && (
            <p className="text-sm text-muted-foreground mt-2">
              Choose a password to activate your admin access.
            </p>
          )}
        </div>
        <form onSubmit={handleSubmit} className="space-y-4 bg-card border border-border rounded-lg p-6">
          <div className="space-y-2">
            <Label htmlFor="password">{isInvite ? "Password" : "New password"}</Label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm">Confirm password</Label>
            <Input
              id="confirm"
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </div>
          {needsCode && (
            <div className="space-y-2">
              <Label htmlFor="code">
                Verification code
                {ready && <span className="ml-2 text-xs text-muted-foreground">(verified)</span>}
              </Label>
              <Input
                id="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                required={!ready}
                disabled={ready}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={ready ? "Verified" : "6-digit code from email"}
              />
            </div>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
          {!ready && (
            <p className="text-xs text-muted-foreground">
              {needsCode
                ? "Enter the verification code from your email to continue."
                : `Waiting for ${isInvite ? "invite" : "recovery"} link… If this persists, request a new email.`}
            </p>
          )}
          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? submittingLabel : submitLabel}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
